export interface CuePoint {
  sectionIndex: number;
  startMs: number;
  endMs: number;
  scrollStart: number;
  scrollEnd: number;
}

export interface ScrollPosition {
  scrollY: number;
  sectionIndex: number;
}

export class TimingEngine {
  private cuePoints: CuePoint[];
  private totalDurationMs: number;

  constructor(sectionDurations: number[], sectionTops: number[], sectionHeights: number[]) {
    let cumulativeMs = 0;
    this.cuePoints = sectionDurations.map((duration, i) => {
      const startMs = cumulativeMs;
      cumulativeMs += duration;
      return {
        sectionIndex: i,
        startMs,
        endMs: cumulativeMs,
        scrollStart: sectionTops[i],
        scrollEnd: sectionTops[i] + sectionHeights[i],
      };
    });
    this.totalDurationMs = cumulativeMs;
  }

  getCuePoints(): CuePoint[] {
    return this.cuePoints;
  }

  getTotalDurationMs(): number {
    return this.totalDurationMs;
  }

  getScrollPosition(timeMs: number): ScrollPosition {
    if (this.cuePoints.length === 0) {
      return { scrollY: 0, sectionIndex: 0 };
    }

    if (timeMs < 0) {
      return { scrollY: this.cuePoints[0].scrollStart, sectionIndex: 0 };
    }

    if (timeMs >= this.totalDurationMs) {
      const last = this.cuePoints[this.cuePoints.length - 1];
      return { scrollY: last.scrollEnd, sectionIndex: last.sectionIndex };
    }

    // Binary search for the section containing timeMs
    const index = this.findSectionIndex(timeMs);
    const cue = this.cuePoints[index];
    const sectionDuration = cue.endMs - cue.startMs;

    if (sectionDuration === 0) {
      return { scrollY: cue.scrollStart, sectionIndex: cue.sectionIndex };
    }

    const progress = (timeMs - cue.startMs) / sectionDuration;
    const scrollRange = cue.scrollEnd - cue.scrollStart;
    const scrollY = cue.scrollStart + progress * scrollRange;

    return {
      scrollY: Math.max(cue.scrollStart, Math.min(cue.scrollEnd, scrollY)),
      sectionIndex: cue.sectionIndex,
    };
  }

  private findSectionIndex(timeMs: number): number {
    let low = 0;
    let high = this.cuePoints.length - 1;

    while (low < high) {
      const mid = (low + high) >>> 1;
      if (this.cuePoints[mid].endMs <= timeMs) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return low;
  }
}
